import { useCallback } from 'react'
import { RPM_FIELD_LETTERS } from '../constants/rpmFieldConstants'
import { compareVersions } from './versionUtils'

// Field definition factories for DRY principles
const createField = (config = {}) => ({
  ...(config.required && { required: config.required }),
  ...(config.min !== undefined && { min: { value: config.min, message: config.minMsg || `Must be at least ${config.min}${config.unit || ''}` } }),
  ...(config.max !== undefined && { max: { value: config.max, message: config.maxMsg || `Must be at most ${config.max}${config.unit || ''}` } }),
  ...(config.step !== undefined && { step: config.step }),
  ...(config.validate && { validate: config.validate }),
})

const percentage = (min, max, required, step = 1) => createField({ min, max, required, step, unit: '%' })
const temperature = (min, max, required, step = 1) => createField({ min, max, required, step, unit: '°C' })
const voltage = (min, max, required, step = 0.1) => createField({ min, max, required, step, unit: 'V' })
const current = (min, max, required, step = 1) => createField({ min, max, required, step, unit: ' amps' })
const time = (min, max, required, step = 1) => createField({ min, max, required, step, unit: ' seconds' })
const selection = (required) => createField({ required })

// Optimized field definitions
const FIELD_DEFINITIONS = {
  alternator: {
    fieldVoltage: percentage(10, 100, 'Field voltage is required'),
    targetTemp: temperature(70, 150, 'Target temperature is required'),
    startupDelay: time(0, 200),
    pulleyRatio: createField({ min: 0.1, max: 10, required: 'Pulley ratio is required', step: 0.01 }),
    altPoles: createField({ min: 1, max: 30, required: 'Number of alternator poles is required' }),
    minField: percentage(0, 25, 'Minimum field drive is required'),
    maxRpm: createField({ min: 0, max: 20000, required: 'Engine maximum RPM is required' }),
    altShuntAmps: current(1, 2000, 'Shunt current rating is required'),
    altShuntMv: createField({ min: 0, max: 160, required: 'Shunt voltage rating is required', unit: ' mV' }),
    altCurrentLimit: current(0, 1000),
    fieldPolarity: selection('Field polarity must be selected'),
  },
  battery: {
    maxChargingTemp: temperature(30, 100, 'Maximum charging temperature is required'),
    minChargingTemp: temperature(-60, 15, 'Minimum charging temperature is required'),
    tempSlope: createField({ min: 0, max: 1000, unit: ' mV/°C' }),
    shuntAmps: current(1, 2000, 'Shunt current rating is required'),
    shuntMv: createField({ min: 0, max: 160, required: 'Shunt voltage rating is required', unit: ' mV' }),
    ampHrCapacity: createField({ min: 10, max: 10000, required: 'Battery bank amp hours is required', unit: ' Ah' }),
    cRate: createField({ min: 0, max: 5, required: 'C-rate is required', step: 0.01 }),
    chargeCutoffPolarity: selection('Charge cutoff polarity must be selected'),
  },
  charging: {
    cvVolts: voltage(6, 60, 'Charging voltage is required'),
    cvMinTime: createField({ min: 1, max: 480, unit: ' minutes' }),
    cvMaxTime: createField({ min: 1, max: 720, unit: ' minutes' }),
    cvFieldPct: percentage(1, 80, 'Field power target is required'),
    cvMinAmps: percentage(1, 50, 'Current minimum is required'),
    postCvVolts: voltage(6, 60, 'Charging voltage is required'),
    postCvMinTime: createField({ min: 1, max: 480, unit: ' minutes' }),
    postCvMaxTime: createField({ min: 1, max: 720, unit: ' minutes' }),
    postCvFieldPct: percentage(1, 80, 'Field power target is required'),
    postCvMinAmps: percentage(1, 50, 'Current minimum is required'),
    floatVolts: voltage(6, 60, 'Charging voltage is required'),
    floatMinTime: createField({ min: 1, max: 480, unit: ' minutes' }),
    floatMaxTime: createField({ min: 1, max: 720, unit: ' minutes' }),
    floatMinVolts: voltage(6, 60),
    floatFieldPctMax: percentage(0, 100),
    floatMaxAmpHrs: percentage(1, 100),
  },
  canbus: {
    chargerInstance: createField({ min: 0, max: 250 }),
    engineInstance: createField({ min: 0, max: 250 }),
    engineLoadThreshold: percentage(30, 100),
    numBatsInBank: createField({ min: 1, max: 10 }),
    batteryInstance: createField({ min: 0, max: 250 }),
    bmsProtocol: {
      validate: (value, formData) => {
        if (!formData?.enableBmsProtocols) return true
        if (!value) return 'BMS protocol selection is required when BMS protocols are enabled'
        return ['none', 'victron'].includes(value) || 'Invalid BMS protocol'
      },
    },
    bmsBitrate: {
      validate: (value, formData) => {
        // Not required if BMS protocols are disabled
        if (!formData?.enableBmsProtocols) return true
        // Not required if Victron is not selected
        if (formData?.bmsProtocol !== 'victron') return true
        // Required when Victron is selected
        if (!value) return 'Bitrate selection is required'
        // Must be valid bitrate
        const bitrate = parseInt(value, 10)
        return [250000, 500000].includes(bitrate) || 'Bitrate must be 250 kbps or 500 kbps'
      },
    },
  },
  network: {
    regulatorPassword: {
      validate: (value) => !value?.trim() || (value.length >= 8 && value.length <= 63) || 'Password must be 8-63 characters long or leave blank for open network',
    },
    networkName: {
      required: 'Network name is required',
      validate: (value) => (value?.trim() && value.length <= 32) || 'Network name is required and must be 32 characters or less',
    },
  },
  system: {
    numAlternatorsInstalled: selection('Number of alternators must be selected'),
    numBatteriesManaged: selection('Battery bank selection is required'),
  },
  rpm: { rpmField: percentage(0, 100) },
  legacy: {
    batteryCapacity: createField({ min: 1, max: 10000, unit: ' Ah' }),
    chargeVoltage: voltage(10, 60),
    floatVoltage: voltage(10, 60),
  },
}

// Efficient rule generation
const buildDualSystemRules = (category) =>
  Object.fromEntries(Object.entries(FIELD_DEFINITIONS[category]).flatMap(([name, rule]) => [[name, rule], [`${name}2`, rule]]))

const generateDynamicFields = () => Object.fromEntries([
  ...'ABCDEFGHIJ'.split('').flatMap(letter => [[`batteryInstance${letter}`, FIELD_DEFINITIONS.canbus.batteryInstance], [`batteryInstance${letter}2`, FIELD_DEFINITIONS.canbus.batteryInstance]]),
  ...RPM_FIELD_LETTERS.flatMap(letter => [`rpmField${letter}`, `rpmField${letter}2`, `rpmField${letter}chart`, `rpmField${letter}chart2`].map(field => [field, FIELD_DEFINITIONS.rpm.rpmField])),
])

// Complete validation rules
export const validationRules = {
  ...buildDualSystemRules('alternator'), ...buildDualSystemRules('battery'), ...buildDualSystemRules('charging'), ...buildDualSystemRules('canbus'),
  ...FIELD_DEFINITIONS.network, ...FIELD_DEFINITIONS.legacy, ...generateDynamicFields(),
  numAlternatorsInstalled: FIELD_DEFINITIONS.system.numAlternatorsInstalled,
  numBatteriesManaged: FIELD_DEFINITIONS.system.numBatteriesManaged,
}

// Streamlined dependencies
const DEPENDENCIES = {
  secondary: {
    alternator: { condition: { field: 'numAlternatorsInstalled', value: 'numAlternatorsInstalled2' }, fields: ['fieldVoltage', 'fieldPolarity', 'targetTemp', 'startupDelay', 'pulleyRatio', 'altPoles', 'minField', 'maxRpm', 'altShuntAmps', 'altShuntMv', 'altCurrentLimit', 'chargerInstance', 'engineInstance', 'engineLoadThreshold'] },
    battery: { condition: { field: 'numBatteriesManaged', value: 'numBatteriesManaged2' }, fields: ['ampHrCapacity', 'cRate', 'maxChargingTemp', 'minChargingTemp', 'tempSlope', 'shuntAmps', 'shuntMv', 'cvVolts', 'cvMinTime', 'cvMaxTime', 'cvFieldPct', 'cvMinAmps', 'postCvVolts', 'postCvMinTime', 'postCvMaxTime', 'postCvFieldPct', 'postCvMinAmps', 'floatVolts', 'floatMinTime', 'floatMaxTime', 'floatMinVolts', 'floatFieldPctMax', 'floatMaxAmpHrs', 'numBatsInBank', 'batteryInstance'] },
  },
  conditional: [
    { check: 'statorInstalled', fields: ['pulleyRatio', 'altPoles', 'minField'] },
    { check: 'tempConnected', fields: ['maxChargingTemp', 'minChargingTemp', 'tempSlope'] },
    { check: 'shuntInstalled', fields: ['shuntAmps', 'shuntMv'] },
    { check: 'altShuntInstalled', fields: ['altShuntAmps', 'altShuntMv'] },
    { check: 'postCvSwitch', fields: ['postCvVolts', 'postCvMinTime', 'postCvMaxTime', 'postCvFieldPct', 'postCvMinAmps'] },
    { check: 'rpmReduceField', fields: ['maxRpm', ...RPM_FIELD_LETTERS.flatMap(l => [`rpmField${l}`, `rpmField${l}chart`])] },
    { check: 'chargeCutoffInstalled', fields: ['chargeCutoffPolarity'] },
  ],
}

// Optimized dependency building
const buildFieldDependencies = () => {
  const showWhenValue = {}

  // Secondary system dependencies
  Object.values(DEPENDENCIES.secondary).forEach(({ condition, fields }) =>
    fields.forEach(field => showWhenValue[`${field}2`] = condition))

  // Conditional dependencies (primary and secondary)
  DEPENDENCIES.conditional.forEach(({ check, fields }) =>
    fields.forEach(field => {
      showWhenValue[field] = { field: check, value: 'checked' }
      showWhenValue[`${field}2`] = { field: `${check}2`, value: 'checked' }
    }))

  // Battery instance dependencies
  Array.from('ABCDEFGHIJ', (letter, index) => {
    const condition = bankIndex => bankIndex >= index
    showWhenValue[`batteryInstance${letter}`] = { field: 'numBatsInBank', value: condition }
    showWhenValue[`batteryInstance${letter}2`] = { field: 'numBatsInBank2', value: condition }
  })

  return {
    showWhenValue: {
      ...showWhenValue,
      networkName: { field: 'networkType', value: type => type !== 'ethernet' },
      regulatorPassword: { field: 'networkType', value: type => type !== 'ethernet' },
      numBatteriesManaged: { field: 'numAlternatorsInstalled', value: 'numAlternatorsInstalled2' },
      bmsProtocol: { field: 'enableBmsProtocols', value: 'checked' },
      bmsBitrate: { field: 'bmsProtocol', value: 'victron' },
      ...Object.fromEntries(['batteryType', 'batteryCapacity', 'batteryVoltage', 'chargeVoltage', 'floatVoltage']
        .map(field => [`${field}2`, { field: 'numAlternatorsInstalled', value: 'numAlternatorsInstalled2' }])),
    },
  }
}

export const fieldDependencies = buildFieldDependencies()

// Optimized validation hook
export const useFormValidation = () => {
  const validateConditionalFields = useCallback(formData =>
    formData.numAlternatorsInstalled === 'numAlternatorsInstalled2'
      ? Object.fromEntries(['fieldVoltage2', 'targetTemp2', 'pulleyRatio2', 'altPoles2', 'minField2']
        .filter(field => !formData[field]?.trim())
        .map(field => [field, { message: `${field} is required when using two alternators` }]))
      : {}, [])

  const validateField = useCallback((fieldName, value) => {
    const rules = validationRules[fieldName]
    if (!rules) return null

    if (rules.required && !value?.toString().trim()) return rules.required
    if (rules.validate) return rules.validate(value) === true ? null : rules.validate(value)
    if (!value?.toString().trim()) return null

    const numValue = parseFloat(value)
    return isNaN(numValue) ? null
      : rules.min?.value > numValue ? rules.min.message
        : rules.max?.value < numValue ? rules.max.message
          : null
  }, [])

  const validateForm = useCallback(formData => {
    const visibleFields = Object.entries(formData)
      .filter(([fieldName]) => {
        return shouldShowField(fieldName, formData)
      })

    const fieldErrors = Object.fromEntries(
      visibleFields
        .map(([fieldName, value]) => {
          const error = validateField(fieldName, value)
          return [fieldName, error]
        })
        .filter(([, error]) => error)
        .map(([fieldName, error]) => [fieldName, { message: error }]),
    )

    const conditionalErrors = validateConditionalFields(formData)

    const result = { ...fieldErrors, ...conditionalErrors }
    return result
  }, [validateField, validateConditionalFields])

  return { validateField, validateForm, validateConditionalFields }
}

// Optimized helper functions using modern ES6+ syntax
export const shouldShowField = (fieldName, formData, deviceInfo = null) => {
  // Hardware version check: fieldPolarity fields hidden for hardware >= 1.1.0
  if ((fieldName === 'fieldPolarity' || fieldName === 'fieldPolarity2') && deviceInfo?.regulatorHardwareVersion) {
    if (compareVersions(deviceInfo.regulatorHardwareVersion, '1.1.0') >= 0) {
      return false
    }
  }

  const dependency = fieldDependencies.showWhenValue[fieldName]

  if (!dependency) {
    return true
  }

  const fieldValue = formData[dependency.field]

  // Special handling for cvFieldPct and postCvFieldPct when shunt is installed
  if ((fieldName === 'cvFieldPct' || fieldName === 'postCvFieldPct') && formData.shuntInstalled) {
    return false
  }
  if ((fieldName === 'cvFieldPct2' || fieldName === 'postCvFieldPct2') && formData.shuntInstalled2) {
    return false
  }

  const result = typeof dependency.value === 'function'
    ? dependency.field.includes('numBatsInBank')
      ? dependency.value(parseInt(fieldValue) || 0)
      : dependency.value(fieldValue)
    : dependency.value === 'checked'
      ? fieldValue === true || fieldValue === 'checked'
      : fieldValue === dependency.value

  return result
}

export const isFieldRequired = (fieldName, formData) =>
  shouldShowField(fieldName, formData) && !!validationRules[fieldName]?.required

// Streamlined field attribute helpers
const getRuleValue = (fieldName, prop, defaultValue) => validationRules[fieldName]?.[prop]?.value ?? defaultValue

export const getFieldStep = (fieldName) => getRuleValue(fieldName, 'step', 1)
export const getFieldMin = (fieldName) => getRuleValue(fieldName, 'min')
export const getFieldMax = (fieldName) => getRuleValue(fieldName, 'max')
export const isNumericField = (fieldName) => {
  const rules = validationRules[fieldName]
  return !!(rules?.min || rules?.max || rules?.step)
}

// Consolidated attribute functions
export const getFieldValidationAttributes = (fieldName, formData = {}) => {
  const rules = validationRules[fieldName]
  return rules ? {
    ...(rules.min && { min: rules.min.value }),
    ...(rules.max && { max: rules.max.value }),
    ...(rules.step && { step: rules.step }),
    ...(rules.pattern && { pattern: rules.pattern.value.source }),
    ...(isFieldRequired(fieldName, formData) && { required: true }),
  } : {}
}

export const getFieldRegistration = (fieldName, formData = {}) => {
  const rules = validationRules[fieldName]
  return rules ? {
    ...(isFieldRequired(fieldName, formData) && {
      required: rules.required || `${fieldName} is required`,
    }),
    ...(rules.min && { min: { value: rules.min.value, message: rules.min.message } }),
    ...(rules.max && { max: { value: rules.max.value, message: rules.max.message } }),
    ...(rules.pattern && { pattern: { value: rules.pattern.value, message: rules.pattern.message } }),
    ...(rules.validate && { validate: rules.validate }),
  } : {}
}
